<template>
  <div class="container">
    <div class="relative isolate z-10 pt-2.5 lg:pt-0">
      <LazyUiBreadcrumbs />
    </div>
    <div
      class="headers mb-6 flex flex-col-reverse gap-4 border-b border-b-black pt-4 lg:flex-row lg:justify-between lg:pb-9 lg:pt-0"
    >
      <div class="flex flex-col-reverse lg:w-1/2">
        <div class="flex flex-col gap-4 pb-4 lg:pb-0">
          <div class="ui-text-m text-gray-90">{{ publishedAt }}</div>
          <div class="flex flex-col gap-2.5">
            <h1 class="ui-h2 text-gray-90">
              {{ blogData?.data?.currentTranslation?.title }}
            </h1>
            <PagesBlogTag
              v-if="blogData?.data?.categories?.[0]?.currentTranslation?.title"
              :tag="blogData?.data?.categories?.[0]?.currentTranslation?.title"
              :color="'#303333'"
            />
          </div>
        </div>
      </div>
      <div class="lg:w-1/2">
        <UiLazyImg
          class="h-[334px] lg:h-[574px] lg:w-full"
          :cover="true"
          :alt="
            image?.alt ||
            blogData?.data?.currentTranslation?.title ||
            $t('image')
          "
          :src="image?.file?.url || recoverPasswordImage"
        />
      </div>
    </div>
    <div class="flex flex-col items-end pt-4 lg:pt-0">
      <template v-if="mappedPostEls?.length">
        <template v-for="(el, idx) in mappedPostEls" :key="idx">
          <div
            v-if="!el.dataset?.['data-insert']"
            class="w-full lg:w-[calc(50%-8px)] [&_h1]:mb-7 [&_h2]:mb-7 [&_h3]:mb-7 [&_h4]:mb-7 [&_li]:mb-3 [&_li]:ml-5 [&_p]:mb-7 [&_ul]:mb-7"
            v-html="el.outerHTML"
          />
          <div
            v-else
            :id="el.dataset?.id"
            class="w-full [&_h1]:mb-7 [&_h2]:mb-7 [&_h3]:mb-7 [&_h4]:mb-7 [&_li]:mb-3 [&_li]:ml-5 [&_p]:mb-7 [&_ul]:mb-7"
          />
        </template>
      </template>
      <div v-else v-html="blogData?.data?.currentTranslation?.description" />
    </div>

    <template v-for="(component, idx) in portalArray">
      <teleport
        v-if="component.data && component.moduleType"
        :key="idx"
        :to="'#' + component.componentName + '-id-' + idx"
      >
        <component
          :is="getComponent(component.componentName, component.moduleType)"
          :name="component.moduleType"
          :block-name="component.componentName"
          :data="component.data"
        ></component>
      </teleport>
    </template>
  </div>
</template>

<script setup lang="ts">
import { parse, type Node } from "node-html-parser";
import { format } from "date-fns";
import { useI18n } from "vue-i18n";
import recoverPasswordImage from "~/assets/images/recoverPassworImage.jpg";

const i18nHead = useLocaleHead({
  addSeoAttributes: true,
});
const { setBlogPostPageLdSchema } = useJsonLdSchema();

interface ImappedPostEls {
  outerHTML: string;
  dataset: { [key: string]: string };
  id: string;
}
interface IportalArray {
  componentName: string;
  uniqId: string;
  moduleType?: string;
  data?: any;
}

const config = useRuntimeConfig();
const generalStore = useGeneralStore();
const route = useRoute();
const { $api } = useNuxtApp();
const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const { visitEvent } = useEvents();
const topElsArray = ref<Node[]>([]);
const mappedPostEls = ref<ImappedPostEls[]>([]);
const portalArray = ref<IportalArray[]>([]);

const { getComponent } = useBlockContainer();

const slug = computed(() => route.params.slug);
const { data: blogData } = await useAsyncCachedData(
  `post-${slug.value as string}`,
  () => {
    return $api.blog.getPostBySlug(slug.value as string);
  },
);

const publishedAt = computed(() => {
  return blogData?.value?.data?.published_at
    ? format(
        new Date(blogData?.value?.data?.published_at),
        generalStore.getSystemDateFormat,
      )
    : "";
});

const image = computed(
  () =>
    blogData?.value?.data?.medias?.find(
      (el) => el.position === "page" || el.position === "list",
    ) || null,
);

const blogPostData = computed(() => blogData.value?.data);

setBreadcrumbs([
  { text: t("home"), href: "/", disabled: false },
  {
    text: t("blog"),
    disabled: false,
    href: "/blog",
  },
  {
    text: blogData.value?.data?.currentTranslation?.title || "",
    disabled: true,
    href: route.path,
  },
]);

if (blogPostData.value) {
  setBlogPostPageLdSchema({
    post: blogPostData,
    image: image?.value?.file?.url || "",
    name: blogPostData.value?.currentTranslation?.title || "",
  });
}

parseHtmlData(blogData.value?.data?.currentTranslation?.description || "");

const { seoTitle, seoDescription } = usePageSeo();

useHead({
  meta: [
    ...metaGenerator({
      title:
        blogData.value?.data?.currentTranslation?.meta_title || seoTitle.value,
      description:
        blogData.value?.data?.currentTranslation?.meta_description ||
        seoDescription.value,
      image:
        blogData.value?.data?.medias?.find((el) => el.position === "open-graph")
          ?.file?.url || "",
      keywords: blogData.value?.data?.currentTranslation?.meta_keywords || "",
      url:
        (generalStore.bootstrap?.settings?.front_domain ||
          config.public.domain) +
        "/blog/" +
        route.params.section +
        "/" +
        route.params.slug,
    }),
    ...(i18nHead.value.meta || []),
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
});

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    blogData.value?.data?.currentTranslation?.meta_description ||
    seoDescription.value,
  ogDescription: () =>
    blogData.value?.data?.currentTranslation?.meta_description ||
    seoDescription.value,
});

function usePageSeo() {
  const seoTitle = computed(() => {
    let title =
      blogData.value?.data?.currentTranslation?.meta_title ||
      blogData.value?.data?.currentTranslation?.title ||
      "";
    if (generalStore.settings?.company_name) {
      title = `${title} | ${generalStore.settings.company_name}`;
    }
    return title;
  });

  const seoDescription = computed(() => {
    const description =
      blogData.value?.data?.currentTranslation?.short_description || "";
    return `${description} ${t("pageMetaDescription")}`;
  });
  return {
    seoTitle,
    seoDescription,
  };
}

onMounted(async () => {
  visitEvent();

  portalArray.value = mappedPostEls.value.reduce((acc, el) => {
    if (el.dataset?.id) {
      acc.push({
        componentName: el.dataset?.["data-insert"] as string,
        uniqId: el.id || "",
      });
    }
    return acc;
  }, [] as IportalArray[]);

  const blockArray = await getComponents(portalArray.value);

  portalArray.value = portalArray.value.map((el) => {
    const component = blockArray.find(
      (block) => block.data.name === el.componentName,
    );
    return {
      ...el,
      data: component?.data.currentTranslation?.content,
      moduleType: component?.data.type.identifier,
    };
  });
});

function parseHtmlData(htmlString) {
  if (!htmlString) {
    return;
  }
  const serverParser = parse(htmlString);

  let activeIdx = 0;
  let idIdx = 0;

  const firstDivEl = parse("<div></div>");
  topElsArray.value.push(firstDivEl.childNodes[0]);

  serverParser.childNodes.forEach((el) => {
    if (!el?.attrs?.["data-insert"]) {
      if (topElsArray.value[activeIdx]?.appendChild) {
        topElsArray.value[activeIdx]?.appendChild(el);
      }
    } else {
      activeIdx = activeIdx + 2;
      const divEl = parse("<div></div>");
      el.setAttribute("id", el.attrs["data-insert"] + "-id-" + idIdx);
      idIdx = idIdx + 1;
      topElsArray.value.push(el);
      topElsArray.value.push(divEl);
    }
  });

  mappedPostEls.value = topElsArray.value.map((el) => {
    return {
      outerHTML: el?.outerHTML || "",
      dataset: { ...el.attrs },
      id: el.id,
    };
  });
}

async function getComponents(portalArrayData: IportalArray[]) {
  const componentNames = portalArrayData.map(
    (component) => component.componentName,
  );
  const uniqueComponentNames = [...new Set(componentNames)];

  const promises = uniqueComponentNames.map((name: string) => {
    return $api.blocks.getBlock(name);
  });

  return await Promise.all(promises);
}
</script>
